// src/services/voice.ts

import { getOpenAIApiKey } from './openai';

export type AssistantLanguage = 'ur' | 'en';

// ============================================
// Language Detection
// ============================================

export function detectLanguage(text: string): 'ur' | 'en' {
  // Check for Urdu script characters
  const urduRegex = /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/;
  
  // Check for Roman Urdu patterns (common Urdu words in English script)
  const romanUrduWords = [
    'hai', 'hoon', 'hain', 'kya', 'kia', 'kiya', 'kaise', 'kahan', 'kyun', 'kaun',
    'ap', 'aap', 'aapka', 'aapki', 'aapke', 'main', 'mein', 'mujhe', 'mera', 'meri',
    'tum', 'hum', 'woh', 'yeh', 'ye', 'acha', 'theek', 'sahi', 'galat', 'nahi',
    'haan', 'ji', 'salam', 'assalam', 'alaikum', 'walikum', 'sallam', 'khuda',
    'hafiz', 'shukriya', 'meherbani', 'muaaf', 'karo', 'karna', 'kar do', 'batao',
    'bata', 'bataiye', 'bataen', 'poochho', 'sunao', 'bolo', 'jao', 'aao', 'khao',
    'piyo', 'so jao', 'utho', 'baitho', 'kijiye', 'karain', 'karta', 'karti',
    'madad', 'chahiye', 'sakta', 'sakti', 'sakte', 'kaam', 'wala', 'wali',
    'raha', 'rahi', 'rahe', 'hojata', 'kerna', 'kerte', 'jaata', 'jaati', 'rahe'
  ];
  
  const lowerText = text.toLowerCase();
  
  // Check for Urdu script
  if (urduRegex.test(text)) {
    return 'ur';
  }
  
  // Check for Roman Urdu
  for (const word of romanUrduWords) {
    if (lowerText.includes(word)) {
      return 'ur';
    }
  }
  
  // Default to English
  return 'en';
}

// ============================================
// Detect language for speech
// ============================================

export function detectSpeechLang(text: string): 'ur' | 'en' {
  return detectLanguage(text);
}

// ============================================
// Pick Urdu Voice from available voices
// ============================================

export function pickUrduVoice(voices: SpeechSynthesisVoice[]): SpeechSynthesisVoice | null {
  if (!voices || voices.length === 0) {
    return null;
  }
  
  // First try to find exact Urdu voice
  for (const voice of voices) {
    const lang = voice.lang.toLowerCase();
    if (lang === 'ur' || lang === 'ur-pk' || lang === 'ur-in') {
      return voice;
    }
  }
  
  // Then try Pakistani or Indian voices
  for (const voice of voices) {
    const lang = voice.lang.toLowerCase();
    if (lang.includes('pk') || lang.includes('in')) {
      return voice;
    }
  }
  
  // Then try any voice that contains Urdu characters in its name
  for (const voice of voices) {
    const name = voice.name.toLowerCase();
    if (name.includes('urdu') || name.includes('pakistan') || name.includes('india')) {
      return voice;
    }
  }
  
  // Fallback to any voice that supports Urdu
  for (const voice of voices) {
    if (voice.lang.includes('ur')) {
      return voice;
    }
  }
  
  // Last resort: return the first available voice
  return voices[0] || null;
}

// ============================================
// Speak with Google TTS (browser native)
// ============================================

export function speakWithGoogleTTS(
  text: string,
  lang: string = 'ur-PK',
  onStart?: () => void,
  onEnd?: () => void,
  onError?: (error: any) => void
): SpeechSynthesisUtterance | null {
  if (!window.speechSynthesis) {
    console.error('Speech synthesis not supported');
    if (onError) onError(new Error('Speech synthesis not supported'));
    return null;
  }
  
  // Cancel any ongoing speech
  window.speechSynthesis.cancel();
  
  try {
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = lang;
    utterance.rate = 0.9;
    utterance.pitch = 1;
    utterance.volume = 1;
    
    // Get available voices
    const voices = window.speechSynthesis.getVoices();
    const urduVoice = pickUrduVoice(voices);
    if (urduVoice) {
      utterance.voice = urduVoice;
    }
    
    if (onStart) {
      utterance.onstart = onStart;
    }
    
    if (onEnd) {
      utterance.onend = onEnd;
    }
    
    if (onError) {
      utterance.onerror = (event) => {
        console.error('TTS Error:', event);
        onError(event);
      };
    }
    
    window.speechSynthesis.speak(utterance);
    return utterance;
  } catch (error) {
    console.error('Error speaking with Google TTS:', error);
    if (onError) onError(error);
    return null;
  }
}

// ============================================
// Speak with OpenAI TTS (requires API key)
// ============================================

export async function speakWithOpenAI(
  text: string,
  voice: 'alloy' | 'echo' | 'fable' | 'onyx' | 'nova' | 'shimmer' = 'nova',
  onStart?: () => void,
  onEnd?: () => void,
  onError?: (error: any) => void
): Promise<HTMLAudioElement | null> {
  const apiKey = getOpenAIApiKey();
  
  if (!apiKey) {
    console.error('OpenAI API key not found for TTS');
    if (onError) onError(new Error('API_KEY_MISSING'));
    return null;
  }
  
  try {
    if (onStart) onStart();
    
    const response = await fetch('https://api.openai.com/v1/audio/speech', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${apiKey}`,
      },
      body: JSON.stringify({
        model: 'tts-1',
        input: text,
        voice: voice,
        response_format: 'mp3',
      }),
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(`TTS API Error: ${response.status} - ${errorData.error?.message || response.statusText}`);
    }
    
    const audioBlob = await response.blob();
    const audioUrl = URL.createObjectURL(audioBlob);
    const audio = new Audio(audioUrl);
    
    if (onStart) {
      audio.onplay = onStart;
    }
    
    if (onEnd) {
      audio.onended = onEnd;
    }
    
    if (onError) {
      audio.onerror = (e) => {
        console.error('Audio playback error:', e);
        onError(e);
      };
    }
    
    await audio.play();
    return audio;
  } catch (error) {
    console.error('Error speaking with OpenAI TTS:', error);
    if (onError) onError(error);
    return null;
  }
}

// ============================================
// Stop OpenAI TTS
// ============================================

export function stopOpenAITTS(audioElement: HTMLAudioElement | null): void {
  if (audioElement) {
    try {
      audioElement.pause();
      audioElement.currentTime = 0;
      URL.revokeObjectURL(audioElement.src);
    } catch (error) {
      console.error('Error stopping OpenAI TTS:', error);
    }
  }
}

// ============================================
// Stop all speech (Google TTS)
// ============================================

export function stopAllSpeech(): void {
  if (window.speechSynthesis) {
    try {
      window.speechSynthesis.cancel();
    } catch (error) {
      console.error('Error stopping speech:', error);
    }
  }
}

// ============================================
// Transcribe with Whisper (OpenAI)
// ============================================

export async function transcribeWithWhisper(
  audioBlob: Blob,
  language: 'ur' | 'en' = 'ur',
  onStart?: () => void,
  onEnd?: () => void,
  onError?: (error: any) => void
): Promise<string> {
  const apiKey = getOpenAIApiKey();
  
  if (!apiKey) {
    const error = new Error('API_KEY_MISSING');
    if (onError) onError(error);
    throw error;
  }
  
  try {
    if (onStart) onStart();
    
    const formData = new FormData();
    formData.append('file', audioBlob, 'audio.webm');
    formData.append('model', 'whisper-1');
    formData.append('language', language === 'ur' ? 'ur' : 'en');
    formData.append('response_format', 'text');
    
    const response = await fetch('https://api.openai.com/v1/audio/transcriptions', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${apiKey}`,
      },
      body: formData,
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(`Transcription Error: ${response.status} - ${errorData.error?.message || response.statusText}`);
    }
    
    const text = await response.text();
    
    if (onEnd) onEnd();
    
    return text.trim();
  } catch (error) {
    console.error('Error transcribing with Whisper:', error);
    if (onError) onError(error);
    throw error;
  }
}

// ============================================
// Speech to Text (Browser Native)
// ============================================

interface BrowserSpeechRecognitionResultItem {
  readonly isFinal: boolean;
  readonly 0: { readonly transcript: string };
}

interface BrowserSpeechRecognitionResultList {
  readonly length: number;
  [index: number]: BrowserSpeechRecognitionResultItem;
}

interface BrowserSpeechRecognitionEvent {
  readonly resultIndex: number;
  readonly results: BrowserSpeechRecognitionResultList;
}

interface BrowserSpeechRecognition extends EventTarget {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  maxAlternatives: number;
  onstart: (() => void) | null;
  onend: (() => void) | null;
  onerror: ((event: any) => void) | null;
  onresult: ((event: BrowserSpeechRecognitionEvent) => void) | null;
  start: () => void;
  stop: () => void;
  abort?: () => void;
}

export function speechToTextNative(
  lang: string = 'ur-PK',
  onResult: (text: string) => void,
  onError: (error: any) => void,
  onEnd?: () => void
): BrowserSpeechRecognition | null {
  const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
  
  if (!SpeechRecognition) {
    console.error('Speech recognition not supported');
    onError(new Error('Speech recognition not supported'));
    return null;
  }
  
  try {
    const recognition = new SpeechRecognition();
    recognition.lang = lang;
    recognition.continuous = false;
    recognition.interimResults = true;
    recognition.maxAlternatives = 1;
    
    let finalText = '';
    let isFinal = false;
    
    recognition.onresult = (event: BrowserSpeechRecognitionEvent) => {
      let interimText = '';
      
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0]?.transcript ?? '';
        
        if (event.results[i].isFinal) {
          finalText += transcript;
          isFinal = true;
        } else {
          interimText += transcript;
        }
      }
      
      if (isFinal && finalText) {
        onResult(finalText);
        // Reset for next utterance
        finalText = '';
        isFinal = false;
      } else if (interimText) {
        // Send interim results
        onResult(interimText);
      }
    };
    
    recognition.onerror = (event: any) => {
      console.error('Speech recognition error:', event.error);
      onError(event.error || new Error('Speech recognition failed'));
    };
    
    recognition.onend = () => {
      if (onEnd) onEnd();
    };
    
    recognition.start();
    return recognition;
  } catch (error) {
    console.error('Error starting speech recognition:', error);
    onError(error);
    return null;
  }
}

// ============================================
// Stop Speech Recognition
// ============================================

export function stopSpeechRecognition(recognition: BrowserSpeechRecognition | null): void {
  if (recognition) {
    try {
      recognition.stop();
    } catch (error) {
      console.error('Error stopping speech recognition:', error);
    }
  }
}

// ============================================
// Utility functions
// ============================================

export function isSpeechRecognitionSupported(): boolean {
  return !!(window.SpeechRecognition || window.webkitSpeechRecognition);
}

export function getSpeechRecognitionErrorMessage(error: string): string {
  const messages: Record<string, string> = {
    'not-allowed': 'Microphone access denied. Please allow microphone access.',
    'no-speech': 'No speech detected. Please try again.',
    'audio-capture': 'No audio captured. Please check your microphone.',
    'network': 'Network error. Please check your internet connection.',
    'aborted': 'Speech recognition was aborted.',
    'language-not-supported': 'Language not supported.',
    'service-not-allowed': 'Speech recognition service not allowed.',
    'not-allowed-mic': 'Microphone permission denied.',
    'audio-busy': 'Audio is busy. Please try again.',
  };
  return messages[error] || `Speech recognition error: ${error}`;
}

export function isVoiceSupported(): boolean {
  return !!(window.speechSynthesis);
}

// ============================================
// Load voices (useful for preloading)
// ============================================

export function loadVoices(): Promise<SpeechSynthesisVoice[]> {
  return new Promise((resolve) => {
    if (!window.speechSynthesis) {
      resolve([]);
      return;
    }
    
    const voices = window.speechSynthesis.getVoices();
    if (voices.length > 0) {
      resolve(voices);
      return;
    }
    
    window.speechSynthesis.onvoiceschanged = () => {
      resolve(window.speechSynthesis.getVoices());
    };
    
    // Timeout fallback
    setTimeout(() => {
      resolve(window.speechSynthesis.getVoices());
    }, 5000);
  });
}

// ============================================
// Export defaults
// ============================================

